
import { useState, useEffect, useCallback, CSSProperties } from 'react';

interface Timeslot {
  start_time: string;
  end_time: string;
}

interface BookingCalendarTexts {
  selectDate: string;
  selectTime: string;
  reviewAndBook: string;
  previousMonth: string;
  nextMonth: string;
  availableSlots: string;
  noSlotsAvailable: string;
  selectedDateTime: string;
  modifyTime: string;
  customerName: string;
  customerPhone: string;
  loanType: string;
  notes: string;
  submitBooking: string;
  submitting: string;
  bookingSuccess: string;
  bookingError: string;
  required: string;
  back: string;
  continue: string;
  step: string;
  of: string;
}

interface BookingCalendarProps {
  timeslotsApi: string;
  appointmentsApi: string;
  texts: BookingCalendarTexts;
  containerStyle?: CSSProperties;
}

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function BookingCalendar({
  timeslotsApi,
  appointmentsApi,
  texts,
  containerStyle
}: BookingCalendarProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [timeslots, setTimeslots] = useState<Timeslot[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<Timeslot | null>(null);
  const [viewMonth, setViewMonth] = useState<number>(new Date().getMonth());
  const [viewYear, setViewYear] = useState<number>(new Date().getFullYear());
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    loan_type: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const fetchTimeslots = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(timeslotsApi);
      const data = await response.json();
      setTimeslots(data || []);
    } catch (error) {
      setTimeslots([]);
    } finally {
      setLoading(false);
    }
  }, [timeslotsApi]);

  useEffect(() => {
    fetchTimeslots();
  }, [fetchTimeslots]);

  // Get available dates from timeslots
  const getAvailableDates = useCallback(() => {
    const dates = new Set<string>();
    timeslots.forEach(slot => {
      const date = slot.start_time.split(' ')[0];
      dates.add(date);
    });
    return Array.from(dates).sort();
  }, [timeslots]);

  // Auto-select closest available date and navigate to its month
  useEffect(() => {
    if (timeslots.length === 0) return;

    const availableDates = getAvailableDates();
    if (availableDates.length === 0) return;

    const today = new Date().toISOString().split('T')[0];
    
    // Find closest available date >= today
    let closestDate = availableDates.find(date => date >= today);
    
    // If no future dates, use the latest available date
    if (!closestDate) {
      closestDate = availableDates[availableDates.length - 1];
    }

    if (closestDate) {
      setSelectedDate(closestDate);
      const [year, month] = closestDate.split('-').map(Number);
      setViewYear(year);
      setViewMonth(month - 1);
    }
  }, [timeslots, getAvailableDates]);

  // Get slots for selected date
  const getSlotsForDate = useCallback((date: string) => {
    return timeslots.filter(slot => slot.start_time.split(' ')[0] === date);
  }, [timeslots]);

  // Calendar generation
  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year: number, month: number) => {
    return new Date(year, month, 1).getDay();
  };

  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth(viewYear, viewMonth);
    const firstDay = getFirstDayOfMonth(viewYear, viewMonth);
    const days: (number | null)[] = [];

    // Add empty cells for days before the first day
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }

    return days;
  };

  const formatDateString = (year: number, month: number, day: number) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const isDateAvailable = (day: number) => {
    const dateStr = formatDateString(viewYear, viewMonth, day);
    return getAvailableDates().includes(dateStr);
  };

  const handlePrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else {
      setViewMonth(viewMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else {
      setViewMonth(viewMonth + 1);
    }
  };

  const handleDateSelect = (day: number) => {
    const dateStr = formatDateString(viewYear, viewMonth, day);
    setSelectedDate(dateStr);
    setSelectedSlot(null);
  };

  const handleSlotSelect = (slot: Timeslot) => {
    setSelectedSlot(slot);
    setCurrentStep(1);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSlot || !formData.customer_name || !formData.customer_phone) return;

    setSubmitting(true);
    setSubmitStatus('idle');

    try {
      const extensions = {
        loan_type: formData.loan_type
      };

      const payload = {
        customer_name: formData.customer_name,
        customer_phone: formData.customer_phone,
        start_time: selectedSlot.start_time,
        end_time: selectedSlot.end_time,
        notes: JSON.stringify({
          message: formData.notes,
          extensions
        })
      };

      const response = await fetch(appointmentsApi, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setSubmitStatus('success');
        // Reset state
        setSelectedDate(null);
        setSelectedSlot(null);
        setFormData({
          customer_name: '',
          customer_phone: '',
          loan_type: '',
          notes: ''
        });
        setCurrentStep(0);
        // Re-fetch timeslots
        await fetchTimeslots();
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (datetime: string) => {
    const timePart = datetime.split(' ')[1];
    return timePart ? timePart.substring(0, 5) : '';
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const availableSlotsForDate = selectedDate ? getSlotsForDate(selectedDate) : [];

  return (
    <div style={containerStyle} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Progress Steps */}
      <div className="bg-[#E8EFF5] px-6 py-4">
        <div className="flex items-center justify-between max-w-md mx-auto">
          {[texts.selectDate, texts.reviewAndBook].map((label, index) => (
            <div key={index} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                currentStep >= index ? 'bg-[#1A3A52] text-white' : 'bg-gray-300 text-gray-600'
              }`}>
                {index + 1}
              </div>
              <span className={`ml-2 text-sm font-medium hidden sm:inline ${
                currentStep >= index ? 'text-[#1A3A52]' : 'text-gray-500'
              }`}>
                {label}
              </span>
              {index < 1 && (
                <div className={`w-12 sm:w-20 h-0.5 mx-2 ${
                  currentStep > index ? 'bg-[#1A3A52]' : 'bg-gray-300'
                }`} />
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="p-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <i className="ri-loader-4-line text-3xl text-[#1A3A52] animate-spin"></i>
          </div>
        ) : (
          <>
            {/* Step 0: Date & Time Selection */}
            {currentStep === 0 && (
              <div className="space-y-6">
                {/* Calendar */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <button
                      onClick={handlePrevMonth}
                      className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                      aria-label={texts.previousMonth}
                    >
                      <i className="ri-arrow-left-s-line text-xl text-gray-600"></i>
                    </button>
                    <h3 className="text-lg font-semibold text-[#1A3A52]">
                      {monthNames[viewMonth]} {viewYear}
                    </h3>
                    <button
                      onClick={handleNextMonth}
                      className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                      aria-label={texts.nextMonth}
                    >
                      <i className="ri-arrow-right-s-line text-xl text-gray-600"></i>
                    </button>
                  </div>

                  {/* Days of week header */}
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {DAYS_OF_WEEK.map(day => (
                      <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
                        {day}
                      </div>
                    ))}
                  </div>

                  {/* Calendar grid */}
                  <div className="grid grid-cols-7 gap-1">
                    {generateCalendarDays().map((day, index) => {
                      if (day === null) {
                        return <div key={index} className="h-10" />;
                      }

                      const dateStr = formatDateString(viewYear, viewMonth, day);
                      const isAvailable = isDateAvailable(day);
                      const isSelected = selectedDate === dateStr;

                      return (
                        <button
                          key={index}
                          onClick={() => isAvailable && handleDateSelect(day)}
                          disabled={!isAvailable}
                          className={`h-10 rounded-lg text-sm font-medium transition-all ${
                            isSelected
                              ? 'bg-[#1A3A52] text-white'
                              : isAvailable
                              ? 'bg-[#E8EFF5] text-[#1A3A52] hover:bg-[#1A3A52] hover:text-white cursor-pointer'
                              : 'text-gray-300 cursor-not-allowed'
                          }`}
                        >
                          {day}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Time Slots */}
                {selectedDate && (
                  <div>
                    <h4 className="text-sm font-semibold text-[#1A3A52] mb-3">
                      {texts.availableSlots} - {selectedDate}
                    </h4>
                    {availableSlotsForDate.length > 0 ? (
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                        {availableSlotsForDate.map((slot, index) => (
                          <button
                            key={index}
                            onClick={() => handleSlotSelect(slot)}
                            className="px-3 py-2 rounded-lg text-sm font-medium bg-[#E8EFF5] text-[#1A3A52] hover:bg-[#1A3A52] hover:text-white transition-colors cursor-pointer whitespace-nowrap"
                          >
                            {formatTime(slot.start_time)}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">{texts.noSlotsAvailable}</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Step 1: Review & Form */}
            {currentStep === 1 && selectedSlot && (
              <div className="space-y-6">
                {/* Selected Time Review */}
                <div className="bg-[#E8EFF5] rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">{texts.selectedDateTime}</p>
                      <p className="text-lg font-semibold text-[#1A3A52]">
                        {selectedSlot.start_time.split(' ')[0]} | {formatTime(selectedSlot.start_time)} - {formatTime(selectedSlot.end_time)}
                      </p>
                    </div>
                    <button
                      onClick={() => setCurrentStep(0)}
                      className="text-sm text-[#14B8A6] hover:underline cursor-pointer whitespace-nowrap"
                    >
                      {texts.modifyTime}
                    </button>
                  </div>
                </div>

                {/* Booking Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="customer_name" className="block text-sm font-medium text-gray-700 mb-1">
                      {texts.customerName} *
                    </label>
                    <input
                      type="text"
                      id="customer_name"
                      name="customer_name"
                      value={formData.customer_name}
                      onChange={handleFormChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A3A52] focus:border-transparent text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="customer_phone" className="block text-sm font-medium text-gray-700 mb-1">
                      {texts.customerPhone} *
                    </label>
                    <input
                      type="tel"
                      id="customer_phone"
                      name="customer_phone"
                      value={formData.customer_phone}
                      onChange={handleFormChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A3A52] focus:border-transparent text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="loan_type" className="block text-sm font-medium text-gray-700 mb-1">
                      {texts.loanType}
                    </label>
                    <select
                      id="loan_type"
                      name="loan_type"
                      value={formData.loan_type}
                      onChange={handleFormChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A3A52] focus:border-transparent text-sm cursor-pointer"
                    >
                      <option value="">Select loan type</option>
                      <option value="Salary-Based Loan">Salary-Based Loan</option>
                      <option value="SME & Working Capital">SME &amp; Working Capital</option>
                      <option value="Microenterprise Loan">Microenterprise Loan</option>
                      <option value="Asset-Backed Loan">Asset-Backed Loan</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                      {texts.notes}
                    </label>
                    <textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleFormChange}
                      maxLength={500}
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A3A52] focus:border-transparent text-sm resize-none"
                    />
                  </div>

                  {submitStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                      <i className="ri-checkbox-circle-line text-green-600 text-xl"></i>
                      <p className="text-green-800 text-sm">{texts.bookingSuccess}</p>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                      <i className="ri-error-warning-line text-red-600 text-xl"></i>
                      <p className="text-red-800 text-sm">{texts.bookingError}</p>
                    </div>
                  )}

                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setCurrentStep(0)}
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {texts.back}
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 px-4 py-3 bg-[#1A3A52] text-white rounded-lg text-sm font-medium hover:bg-[#2D5A7B] transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap"
                    >
                      {submitting ? texts.submitting : texts.submitBooking}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
